import type {
  User,
  Model3D,
  Category,
  Brand,
  Favorite,
  UserModel,
  PaginatedResponse,
  ModelSearchParams,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
} from '@visionar/shared';

// Detect if running in Capacitor native app
const isCapacitor = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check for Capacitor object (most reliable)
  if ('Capacitor' in window && (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()) {
    return true;
  }

  // Fallback: Check URL patterns used by Capacitor
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;

  // Capacitor uses capacitor://, ionic://, or https://localhost on Android
  if (protocol === 'capacitor:' || protocol === 'ionic:') {
    return true;
  }

  // Android with androidScheme: 'https' uses https://localhost
  if (hostname === 'localhost' && (protocol === 'https:' || protocol === 'http:')) {
    // Check if NOT running on a dev server (which also uses localhost but with a port)
    const port = window.location.port;
    if (!port || port === '443' || port === '80') {
      return true;
    }
  }

  return false;
};

const isNative = isCapacitor();

// Log for debugging (remove in production)
if (typeof window !== 'undefined') {
  console.log('[API] isNative:', isNative, 'protocol:', window.location.protocol, 'hostname:', window.location.hostname, 'port:', window.location.port);
}

const API_BASE = isNative
  ? 'https://digimon.site/api'
  : '/api';

// Auth token for native apps (cookies don't persist on Capacitor)
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  console.log('[API] Auth token set:', token ? 'yes' : 'no');
}

export function getAuthToken(): string | null {
  return authToken;
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

class ApiError extends Error {
  constructor(
    public statusCode: number,
    public error: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, params } = options;

  let url = `${API_BASE}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Build headers with auth token if available
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(response.status, data.error, data.message);
  }

  return data;
}

// Auth API
export const authApi = {
  async register(data: RegisterRequest): Promise<AuthResponse> {
    return request('/auth/register', { method: 'POST', body: data });
  },

  async login(data: LoginRequest): Promise<AuthResponse> {
    return request('/auth/login', { method: 'POST', body: data });
  },

  async logout(): Promise<{ success: boolean }> {
    return request('/auth/logout', { method: 'POST' });
  },

  async me(): Promise<{ user: User | null }> {
    return request('/auth/me');
  },
};

// Models API
export const modelsApi = {
  async list(params?: ModelSearchParams): Promise<PaginatedResponse<Model3D>> {
    return request('/models', { params: params as Record<string, string | number | boolean | undefined> });
  },

  async get(id: string): Promise<{ data: Model3D }> {
    return request(`/models/${id}`);
  },

  async featured(): Promise<{ data: Model3D[] }> {
    return request('/models/featured');
  },

  async search(q: string, page = 1, limit = 20): Promise<PaginatedResponse<Model3D>> {
    return request('/models/search', { params: { q, page, limit } });
  },

  async byCategory(slug: string): Promise<{ category: Category; data: Model3D[] }> {
    return request(`/models/category/${slug}`);
  },
};

// Categories API
export const categoriesApi = {
  async list(): Promise<{ data: Category[] }> {
    return request('/categories');
  },

  async get(slug: string): Promise<{ data: Category }> {
    return request(`/categories/${slug}`);
  },
};

// Brands API
export const brandsApi = {
  async list(): Promise<{ data: Brand[] }> {
    return request('/brands');
  },

  async get(slug: string): Promise<{ data: Brand }> {
    return request(`/brands/${slug}`);
  },
};

// Favorites API
export const favoritesApi = {
  async list(): Promise<{ data: Favorite[] }> {
    return request('/favorites');
  },

  async add(modelId: string): Promise<{ data: Favorite }> {
    return request(`/favorites/${modelId}`, { method: 'POST' });
  },

  async remove(modelId: string): Promise<{ success: boolean }> {
    return request(`/favorites/${modelId}`, { method: 'DELETE' });
  },

  async check(modelId: string): Promise<{ isFavorited: boolean }> {
    return request(`/favorites/check/${modelId}`);
  },
};

// Users API
export const usersApi = {
  async profile(): Promise<{ data: User & { stats: { favoritesCount: number; capturesCount: number } } }> {
    return request('/users/profile');
  },

  async updateProfile(data: { name?: string; avatar?: string | null }): Promise<{ data: User }> {
    return request('/users/profile', { method: 'PATCH', body: data });
  },

  async recent(): Promise<{ data: Array<{ model: Model3D; viewedAt: Date }> }> {
    return request('/users/recent');
  },

  async captures(): Promise<{ data: Array<{ id: string; type: string; url: string; model: Model3D }> }> {
    return request('/users/captures');
  },
};

// User Models API
export const userModelsApi = {
  async upload(
    file: File,
    name: string,
    description?: string,
    isPublic = false
  ): Promise<{ data: UserModel }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (description) formData.append('description', description);
    formData.append('isPublic', String(isPublic));

    // Build headers with auth token if available
    const headers: Record<string, string> = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_BASE}/user-models/upload`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(response.status, data.error, data.message);
    }

    return data;
  },

  async getMyModels(): Promise<{ data: UserModel[] }> {
    return request('/user-models');
  },

  async getPublicModels(): Promise<{ data: UserModel[] }> {
    return request('/user-models/public');
  },

  async getModel(id: string): Promise<{ data: UserModel }> {
    return request(`/user-models/${id}`);
  },

  async updateModel(
    id: string,
    updates: { name?: string; description?: string | null; isPublic?: boolean }
  ): Promise<{ data: UserModel }> {
    return request(`/user-models/${id}`, { method: 'PATCH', body: updates });
  },

  async deleteModel(id: string): Promise<{ success: boolean }> {
    return request(`/user-models/${id}`, { method: 'DELETE' });
  },
};

export { ApiError };
